import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  styled,
  InputAdornment,
  Badge,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Notifications from './Notifications';
import logo from '../logo.jpg';
import { useLocation } from 'react-router-dom'; // Already imported

const Search = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  padding: '0 10px',
  borderRadius: '9px',
  width: '20%',
  height: '33px',
  [theme.breakpoints.down('sm')]: {
    width: '25%',
  },
}));

export default function Navbar({
  handleDrawerToggle,
  toggleDarkMode,
  darkMode,
  toggleCartDrawer,
  cartItemCount,
  searchQuery,
  setSearchQuery,
  role = '',
  userId,
  handleLogout,
  notificationCount = 0,
  notifications = [], 
  billingPreference   
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation(); // Used to check current path

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openNotificationsDialog, setOpenNotificationsDialog] = useState(false);

  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleConfirmLogout = () => {
    handleLogout();
    setOpenDialog(false);
  };

  const handleNotificationsOpen = () => {
    setOpenNotificationsDialog(true);
  };

  const handleNotificationsClose = () => {
    setOpenNotificationsDialog(false);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Hide cart icon when on landing page (/)
  //const showCartIcon = role.toLowerCase() === 'customer' && location.pathname !== '/';

  const isBillingBypass =
  billingPreference === 0 || billingPreference === 1;

const showCartIcon =
  role.toLowerCase() === 'customer' &&
  location.pathname !== '/' &&
  !location.pathname.includes('/join-cart') &&
  !isBillingBypass;
  // Hide profile and notification icons when on landing page (/)
  const showProfileAndNotifications = role && location.pathname !== '/';

  return (
    <>
      <AppBar position="fixed" sx={{ height: '72px' }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <IconButton color="inherit" onClick={handleDrawerToggle} edge="start" sx={{ mr: isMobile ? 1 : 2 }}>
            <MenuIcon />
          </IconButton>

          <img src={logo} width={20} height={20} />

          <Typography variant="h6" sx={{ flexGrow: 1, m: 1 }}>
            Platterly
          </Typography>

          {role.toLowerCase() === 'customer' ? (
            <>
              {/* <Search>
                <InputBase
                  placeholder="Search..."
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{
                    input: {
                      color: '#000',
                      '::placeholder': { color: '#888' },
                    },
                  }}
                  endAdornment={
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: 'gray' }} />
                    </InputAdornment>
                  }
                />
              </Search> */}
              {showCartIcon && (
                <IconButton onClick={toggleCartDrawer} color="inherit" sx={{ ml: isMobile ? 0 : 2 }}>
                  <Badge badgeContent={cartItemCount} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              )}
            </>
          ) : showProfileAndNotifications ? (
            <>
              <IconButton color="inherit" sx={{ ml: isMobile ? 1 : 2 }} onClick={handleNotificationsOpen}>
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ ml: isMobile ? 1 : 2 }}
              >
                <AccountCircleIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={isMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 2,
                    minWidth: '200px',
                  },
                }}
              >
                <MenuItem disabled sx={{ py: 1.5 }}>
                  <Typography variant="h6">{userId || 'User'}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogoutClick} sx={{ py: 1.5 }}>
                  <LogoutIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                  <Typography variant="h6">Logout</Typography>
                </MenuItem>
              </Menu>
            </>
          ) : null}

          <IconButton onClick={toggleDarkMode} color="inherit" sx={{ ml: isMobile ? 0 : 2 }}>
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography id="logout-dialog-description">
            Are you sure you want to logout?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmLogout} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Notifications
        open={openNotificationsDialog}
        onClose={handleNotificationsClose}
        notifications={notifications}
        role={role}
      />
    </>
  );
}